import { UAParser } from 'ua-parser-js';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { cn } from '@/lib/utils';

/** Parsed once at module load — the host OS can't change mid-session.
 *
 *  ua-parser-js is PINNED to an exact 1.0.x on purpose, and the pin is load-
 *  bearing twice over: v2 relicensed from MIT to AGPL-3.0-or-later, whose
 *  network clause reaches hosted SaaS like this app, and this package was
 *  supply-chain compromised in 2021 — neither is something a range should be
 *  able to pull in unattended. Read `license.md` before changing the spec. */
const UA = new UAParser().getResult();

/** Whether react-hotkeys-hook's `mod` resolves to metaKey (⌘) rather than
 *  ctrlKey — the ONLY question this module is answering. It is deliberately
 *  not "is this an Apple device".
 *
 *  react-hotkeys-hook 5.3.2 decides with:
 *
 *    /mac/i.test(navigator.userAgent) && !/iphone|ipad|ipod/i.test(navigator.userAgent)
 *
 *  so iOS and iPadOS bind ctrlKey, NOT ⌘ — even on an iPad with a Magic
 *  Keyboard, which is the intuitive-but-wrong answer. `os.name === 'Mac OS'`
 *  reproduces that exactly (ua-parser reports iPhone/iPad as 'iOS'), while
 *  reading as a statement about the platform rather than a double negative.
 *
 *  If this ever drifts from the library the hint becomes confidently wrong,
 *  so it is pinned by a behavioural test that fires the advertised modifier
 *  through a real `useHotkeys('mod+enter')` binding — see the spec. */
const MOD_IS_META = UA.os.name === 'Mac OS';

/** Device class, for surfaces that need to branch on form factor —
 *  `undefined` on desktop, which is how ua-parser reports it. Exported so
 *  mobile/tablet-specific behaviour (e.g. suppressing shortcut hints on a
 *  device with no attached keyboard) has a single source to build on. */
export const DEVICE_TYPE = UA.device.type;

/** The "mod" key react-hotkeys-hook binds, as the user should read it. Keep
 *  this the single source of truth for how the modifier is SPELLED, so a hint
 *  can never advertise a key the handler doesn't listen for. */
export const MOD_KEY = MOD_IS_META ? '⌘' : 'Ctrl';

/** Value for the `aria-keyshortcuts` attribute on the control a hint labels —
 *  the spec's key names, not the rendered glyphs. */
export const MOD_KEYSHORTCUT = MOD_IS_META ? 'Meta' : 'Control';

/** Chips for the `mod+enter` combo — the most common "commit / add another"
 *  shortcut in the app. Shared so every surface spells it identically. */
export const MOD_ENTER_KEYS = [MOD_KEY, '↵'] as const;

/**
 * A shortcut hint rendered inside the control it belongs to: `["⌘", "↵"]` → ⌘ ↵.
 *
 * Styling comes from shadcn's `Kbd`/`KbdGroup` (G9 — never restyle those here).
 * This wrapper adds the two things shadcn's primitives deliberately don't do:
 *
 *  - `aria-hidden`, so the glyphs stay out of the host control's accessible
 *    name — a button labelled "Add row" must not announce as "Add row ⌘ ↵".
 *    Put the machine-readable form on the control with `aria-keyshortcuts`.
 *  - the platform-correct spelling of the modifier, via `MOD_KEY` above.
 */
export function ShortcutHint({ keys, className }: { keys: readonly string[]; className?: string }) {
  return (
    <KbdGroup aria-hidden="true" className={cn('pointer-events-none', className)}>
      {keys.map((k, i) => (
        // Index in the key: a combo may legitimately repeat a glyph (e.g.
        // a chord spelled ["⌘", "K", "⌘", "K"]), and a bare `key={k}` would
        // collide. The list is a fixed, statically-ordered constant — never
        // reordered or filtered — so the index is stable.
        <Kbd key={`${i}-${k}`}>{k}</Kbd>
      ))}
    </KbdGroup>
  );
}
